import React, { useEffect, useRef, useState } from "react";
import questions from "../../../utils/QuestionSection";
import { FaArrowLeft, FaArrowRight, FaPaperPlane } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

import {
  config,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
  successNotify,
  updateValueInLocalStorage,
} from "../../../utils/helper";
import {
  authorizedGet,
  authorizedPost,
} from "../../../Config/networkWithToken";
import configuration from "../../../Config/configuration";
import Spinner from "../../Spinner/Spinner";
import QuestionnaireGuidelines from "./QuestionnaireGuidelines";

const QuestionnaireForm = ({
  currentStep,
  setCurrentStep,
  steps,
  userDetails,
  userRole,
  setUserDetails,
  setIsNext,
}) => {
  const sectionKeys = Object.keys(questions);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const containerRef = useRef(null);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);   
  const [isSubmitAgreed, setIsSubmitAgreed] = useState(false);

  const currentSectionKey = sectionKeys[currentSectionIndex];
  const currentSection = questions[currentSectionKey];
  const userId = getValueFromLocalStorage(localStorageKey.USERDATA, "_id");
  const role = getValueFromLocalStorage(localStorageKey.USERDATA, "role");
  const userData = getValueFromLocalStorage(
    localStorageKey.USERDATA,
    "profile",
  );

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, [currentSectionIndex]);

  useEffect(() => {
    if (Object.entries(formData).length === 0) {
      fetchQuestionAnswers();
    }
  }, []);

  const fetchQuestionAnswers = async () => {
    try {
      const response = await authorizedGet(
        `${configuration.apis.getAnswers}?userId=${userId ? userId : userDetails?._id}`,
      );

      if (response?.data?.status) {
        const { answers, completionPercentage, isAgreed } = response.data.data;
        setCompletionPercentage(completionPercentage);
        setIsModalOpen(isAgreed ? false : true);
        setIsAgreed(isAgreed || false);

        const mappedFormData = {};

        answers.forEach((section) => {
          const sectionKey = section.sectionId;
          mappedFormData[sectionKey] = {};

          section.questions.forEach((q) => {
            mappedFormData[sectionKey][q.id] = q.answer;
          });
        });

        setFormData(mappedFormData);
        setOriginalData(mappedFormData);
      }
    } catch (error) {
      console.log(error);
      setIsModalOpen(true);
      setIsAgreed(false);
    }
  };

  const handleInputChange = (sectionKey, qId, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [qId]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSectionIndex < sectionKeys.length - 1) {
      setCurrentSectionIndex((prev) => prev + 1);
      setCurrentQuestionPage(0);
    }
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex((prev) => prev - 1);
      setCurrentQuestionPage(0);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!areAllQuestionsAnswered()) {
        errorNotify("Please answer all questions before proceeding.");
        return;
      }
      setIsButtonLoading(true);
      const percentage = calculateProgressPercentage();
      // const answers = Object.entries(formData).map(
      //   ([sectionKey, questionsMap]) => {
      //     return {
      //       sectionId: sectionKey,
      //       title: currentSection.title,
      //       questions: Object.entries(questionsMap).map(([id, answer]) => ({
      //         id,
      //         answer,
      //       })),
      //       sectionCompletionPercentage: getSectionProgress(sectionKey),
      //     };
      //   },
      // );
      const answers = Object.entries(formData).map(
        ([sectionKey, questionsMap]) => {
          const cleanedQuestions = Object.entries(questionsMap).map(
            ([id, answer]) => {
              // Remove numeric keys inside textFieldForEach answers
              if (
                typeof answer === "object" &&
                answer !== null &&
                Object.keys(answer).some((k) => !isNaN(k))
              ) {
                // Keep only non-numeric keys
                const filtered = Object.fromEntries(
                  Object.entries(answer).filter(([key]) => isNaN(Number(key))),
                );
                return { id, answer: filtered };
              }
              return { id, answer };
            },
          );

          return {
            sectionId: sectionKey,
            title: currentSection.title,
            questions: cleanedQuestions,
            sectionCompletionPercentage: getSectionProgress(sectionKey),
          };
        },
      );

      const data = {
        userId: userId ? userId : userDetails?._id,
        answers: answers,
        percentage: percentage,
        isAgreed: true,
      };
      console.log("user", userDetails?.profile?.questionnaire);

      const response = await authorizedPost(
        configuration.apis.saveQuestions,
        data,
        config,
      );
      const res = response?.data?.data;

      if (response?.data?.status) {
        if (!userData?.questionnaire && role === "user") {
          handleSaveSection(res?._id, res?.completionPercentage);
        }
        if (
          !userDetails?.profile?.questionnaire &&
          (role === "admin" || role === "superAdmin")
        ) {
          handleSaveSection(res?._id, res?.completionPercentage);
        }
        if (
          currentSectionIndex === sectionKeys.length - 1 &&
          currentQuestionPage === totalQuestionPages - 1
        ) {
          if (res?.completionPercentage === 100) {
            handleSaveSection(res?._id, res?.completionPercentage);
          } else {
            errorNotify("Please complete all questions before proceeding.");
            // handleNextSection("next");
          }
        } else if (currentQuestionPage === totalQuestionPages - 1) {
          handleNext();
        }
      } else {
        errorNotify(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsButtonLoading(false);
    }
  };

  // const handleClick = (index) => {
  //   setCurrentSectionIndex(index);
  //   setCurrentQuestionPage(0);
  // };

  //section completion percentage
  // const getSectionProgress = (sectionKey) => {
  //   const answeredCount = Object.keys(formData[sectionKey] || {}).length;
  //   const totalCount = questions[sectionKey]?.questions.length || 0;

  //   const percentage =
  //     totalCount === 0 ? 0 : Math.round((answeredCount / totalCount) * 100);

  //   return percentage;
  // };
  const getSectionProgress = (sectionKey) => {
    const sectionQuestions = questions[sectionKey]?.questions || [];
    const answeredQuestions = formData[sectionKey] || {};

    // Only count answers that exist in the visible questions
    const answeredCount = sectionQuestions.filter(
      (q) =>
        answeredQuestions[q.id] !== undefined && answeredQuestions[q.id] !== "",
    ).length;

    const totalCount = sectionQuestions.length;

    return totalCount === 0
      ? 0
      : Math.round((answeredCount / totalCount) * 100);
  };

  //overall completion percentage
  // const calculateProgressPercentage = () => {
  //   let totalQuestions = 0;
  //   let completedQuestions = 0;

  //   Object.keys(questions).forEach((sectionKey) => {
  //     const sectionQuestions = questions[sectionKey]?.questions || [];

  //     totalQuestions += sectionQuestions.length;

  //     const answeredQuestions = formData[sectionKey] || {};
  //     const answeredCount = Object.keys(answeredQuestions).length;

  //     completedQuestions += answeredCount;
  //   });

  //   if (totalQuestions === 0 || completedQuestions === 0) return 0;

  //   return Math.round((completedQuestions / totalQuestions) * 100);
  // };
  const calculateProgressPercentage = () => {
    let totalQuestions = 0;
    let completedQuestions = 0;

    Object.keys(questions).forEach((sectionKey) => {
      const sectionQuestions = questions[sectionKey]?.questions || [];
      const answeredQuestions = formData[sectionKey] || {};

      totalQuestions += sectionQuestions.length;

      completedQuestions += sectionQuestions.filter(
        (q) =>
          answeredQuestions[q.id] !== undefined &&
          answeredQuestions[q.id] !== "",
      ).length;
    });

    return totalQuestions === 0
      ? 0
      : Math.round((completedQuestions / totalQuestions) * 100);
  };

  const isPageModified = () => {
    const currentPageQuestions = currentSection.questions?.slice(start, end);
    const savedAnswers = originalData[currentSectionKey] || {};
    const currentAnswers = formData[currentSectionKey] || {};

    const previousAnswers = {};
    currentPageQuestions.forEach((q) => {
      const savedAnswer = savedAnswers[q.id];
      if (savedAnswer !== undefined) {
        previousAnswers[q.id] = savedAnswer;
      }
    });

    const isModified = currentPageQuestions.some((q) => {
      return currentAnswers[q.id] !== previousAnswers[q.id];
    });

    return isModified;
  };

  const handleNextSection = (direction) => {
    if (direction === "next" && currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    } else if (direction === "prev" && currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveSection = async (id, percentage) => {
    setIsButtonLoading(true);
    const data = {
      questionnaire: {
        isQuestionnaireForm: true,
        questionnaireFormId: id,
      },
    };

    try {
      let res = [];
      if (userRole === "admin" || userRole === "superAdmin") {
        res = await authorizedPost(
          `${configuration.apis.saveProfile}?editUserId=${userDetails?._id}`,
          data,
          config,
        );
      } else {
        res = await authorizedPost(
          configuration.apis.saveProfile,
          data,
          config,
        );
      }
      if (res.data.status) {
        if (percentage === 100) handleNextSection("next");
        const updatedProfileData = res?.data?.data;
        const profileCompletionPercentage =
          res?.data?.profileCompletionPercentage;

        if (userRole === "admin" || userRole === "superAdmin") {
          setUserDetails({
            ...userDetails,

            profile: {
              ...userDetails.profile,

              questionnaire: { ...updatedProfileData?.profile?.questionnaire },
            },
          });
        } else {
          if (updatedProfileData) {
            Object.keys(updatedProfileData).forEach((field) => {
              updateValueInLocalStorage(
                "userData",
                field,
                updatedProfileData[field],
              );
            });
          }
          if (profileCompletionPercentage) {
            updateValueInLocalStorage(
              "userData",
              "profileCompletionPercentage",
              profileCompletionPercentage,
            );
          }
        }
      } else {
        console.log("API call failed:", res.data.message);
      }
    } catch (error) {
      console.error("API call failed:", error);
    } finally {
      setIsButtonLoading(false);
    }
  };
  //Questions pagniation logic
  const [currentQuestionPage, setCurrentQuestionPage] = useState(0);

  const QUESTIONS_PER_PAGE = 10;
  const start = currentQuestionPage * QUESTIONS_PER_PAGE;
  const end = (currentQuestionPage + 1) * QUESTIONS_PER_PAGE;
  const currentQuestions = currentSection?.questions?.slice(start, end);

  const totalQuestionPages = Math.ceil(
    currentSection.questions.length / QUESTIONS_PER_PAGE,
  );
  const areAllQuestionsAnswered = () => {
    const currentPageQuestions = currentSection.questions?.slice(start, end);
    const currentAnswers = formData[currentSectionKey] || {};

    return currentPageQuestions.every((q) => {
      const value = currentAnswers[q.id];
      if (value === undefined || value === null) return false;

      // Handle text / dropdown (simple string)
      if (typeof value === "string") return value.trim() !== "";

      if (q.multiSelect) {
        if (!value || typeof value !== "object") return false;

        const selected = Array.isArray(value.selected) ? value.selected : [];
        const othersText = value.othersText || "";

        // ✅ Must have at least one selected OR a valid "Others" text
        const hasAtLeastOne = selected.length > 0;
        const hasValidOthers =
          (selected.includes("Others") && othersText.trim() !== "") ||
          (!selected.includes("Others") && hasAtLeastOne);

        return hasValidOthers;
      }

      // Handle textFieldForEach
      if (q.optionType === "textFieldForEach") {
        // All text fields must be filled
        return (
          Object.keys(value).length === q.options.length &&
          Object.values(value).every((v) => v && v.trim() !== "")
        );
      }
      // Handle showTextOnOthers
      if (q.showTextOnOthers) {
        if (typeof value === "object") {
          if (value.option === "Others") {
            return value.othersText && value.othersText.trim() !== "";
          }
          return value.option && value.option.trim() !== "";
        }
      }

      // Handle showSubFieldsOnYes
      if (q.showSubFieldsOnYes) {
        if (typeof value === "object") {
          if (value.option === "Yes") {
            return value.subSelection && value.subSelection.trim() !== "";
          }
          return value.option && value.option.trim() !== "";
        }
      }

      // Default: assume answered
      return true;
    });
  };

  const handlePagnination = (direction) => {
    if (direction === "forward") {
      if (!areAllQuestionsAnswered()) {
        errorNotify("Please answer all questions before proceeding.");
        return;
      }
      setCurrentQuestionPage((prev) =>
        Math.min(prev + 1, totalQuestionPages - 1),
      );
      if (isPageModified()) {
        handleSubmit();
      } else {
        if (currentQuestionPage === totalQuestionPages - 1) {
          handleNext();
        }
      }
    } else if (direction === "backward") {
      setCurrentQuestionPage((prev) => Math.max(prev - 1, 0));
      if (currentQuestionPage === 0) {
        handleBack();
      }
    }
  };
  return (
    <div ref={containerRef} className="font-jost max-w-8xl mx-auto p-6 ">
      {/* Section Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-textYellow sm:text-3xl">
          Section {currentSectionIndex + 1} of {sectionKeys.length}
        </h2>
        <p className="mt-2 text-base font-medium text-gray-600 sm:text-lg">
          {currentSection.title}
        </p>

        {/* Section Progress Circles */}
        <div className="mt-6 overflow-x-auto">
          <div className="flex min-w-max justify-center px-4">
            <div className="flex items-center space-x-4">
              {sectionKeys.map((section, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`relative h-6 w-6 flex-shrink-0 rounded-full border-2 transition-all duration-300 ms:h-8 ms:w-8 xxs:h-12 xxs:w-12
                    ${
                      index === currentSectionIndex
                        ? " border-textYellow bg-textYellow text-white hover:border-[3px] hover:border-green-500"
                        : index < currentSectionIndex
                          ? "border-green-500 bg-green-500 text-white hover:border-[3px] hover:border-textYellow"
                          : "border-gray-300 bg-white text-gray-500 hover:border-[3px] hover:border-textYellow"
                    } flex cursor-pointer items-center justify-center text-sm font-semibold  sm:text-base`}
                  >
                    {index + 1}
                  </div>
                  {index < sectionKeys.length - 1 && (
                    <div
                      className={`h-1 w-2 transition-all duration-300 sm:w-10 md:w-12 ${
                        index < currentSectionIndex
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="mt-10 space-y-2">
        {currentQuestions?.map((q) => {
          const value = formData[currentSectionKey]?.[q.id] || "";

          return (
            <div
              key={q.id}
              className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <label className="mb-2 block font-semibold text-gray-800">
                {q.id}. {q.question}
              </label>

              {/* Dynamic UI logic starts here */}
              {q.optionType === "radioGroup" && q.optionGroups ? (
                // Radio Group
                <div className="space-y-3">
                  {q.optionGroups.map((group, idx) => {
                    const selectedGroupValues = value || {}; // ensures object storage per group
                    return (
                      <div key={idx}>
                        <p className="font-medium text-gray-700">
                          {group.title}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-4">
                          {group.options.map((opt, i) => (
                            <label key={i} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`q-${q.id}-${group.title}`} // unique per group
                                checked={
                                  selectedGroupValues[group.title] === opt
                                }
                                onChange={() => {
                                  const updated = {
                                    ...(selectedGroupValues || {}),
                                  };
                                  updated[group.title] = opt;
                                  handleInputChange(
                                    currentSectionKey,
                                    q.id,
                                    updated,
                                  );
                                }}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : q.optionType === "textFieldForEach" && q.options ? (
                // Text field per option
                <div className="space-y-3">
                  {q.options.map((opt, index) => (
                    <div key={index} className="flex flex-col gap-1">
                      <label className="font-medium text-gray-700">{opt}</label>
                      <textarea
                        type={
                          q.optionValueType === "numeric" ? "number" : "text"
                        }
                        min={q.optionValueType === "numeric" ? 0 : undefined}
                        max={q.optionValueType === "numeric" ? 10 : undefined}
                        className="rounded-lg border border-textYellow p-2 focus:outline-none"
                        placeholder={
                          q.optionValueType === "numeric"
                            ? "Enter value (0–10)"
                            : "Type your answer"
                        }
                        value={value?.[opt] || ""}
                        onChange={(e) => {
                          const inputVal = e.target.value;

                          // For numeric questions → enforce 0–10 range
                          if (q.optionValueType === "numeric") {
                            const numVal = Number(inputVal);

                            // If it's not a number or outside range, skip update
                            if (isNaN(numVal) || numVal < 0 || numVal > 10)
                              return;
                          }
                          let currentValue = value;
                          if (
                            typeof currentValue !== "object" ||
                            Array.isArray(currentValue)
                          ) {
                            currentValue = {}; // reset if string or invalid
                          }

                          const updated = { ...currentValue, [opt]: inputVal };
                          handleInputChange(currentSectionKey, q.id, updated);
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : q.multiSelect && q.options ? (
                // Multi-select with "Others"
                (() => {
                  const selected = value?.selected || value || [];
                  const hasOthers =
                    Array.isArray(selected) && selected.includes("Others");
                  return (
                    <div className="space-y-2">
                      {q.options.map((opt, idx) => (
                        <label key={idx} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={
                              Array.isArray(selected) && selected.includes(opt)
                            }
                            onChange={() => {
                              let updated = Array.isArray(selected)
                                ? [...selected]
                                : [];
                              if (updated.includes(opt))
                                updated = updated.filter((o) => o !== opt);
                              else updated.push(opt);
                              handleInputChange(currentSectionKey, q.id, {
                                selected: updated,
                                othersText: value?.othersText || "",
                              });
                            }}
                          />
                          {opt}
                        </label>
                      ))}
                      {hasOthers && (
                        <input
                          type="text"
                          placeholder="Please specify"
                          className="mt-2 w-full rounded-lg border border-textYellow p-2 focus:outline-none"
                          value={value?.othersText || ""}
                          onChange={(e) =>
                            handleInputChange(currentSectionKey, q.id, {
                              selected,
                              othersText: e.target.value,
                            })
                          }
                        />
                      )}
                    </div>
                  );
                })()
              ) : q.showTextOnOthers && q.options ? (
                // Single-select with text input for "Others"
                (() => {
                  const selected = value?.option || value || "";
                  const showOthersField = selected === "Others";
                  return (
                    <div className="space-y-3">
                      {/* Main dropdown or radio selection */}
                      <select
                        className="w-full rounded-lg border border-textYellow p-2 focus:outline-none"
                        value={selected}
                        onChange={(e) =>
                          handleInputChange(currentSectionKey, q.id, {
                            option: e.target.value,
                            othersText:
                              e.target.value === "Others"
                                ? value?.othersText || ""
                                : "",
                          })
                        }
                      >
                        <option value="">Select an option</option>
                        {q.options.map((opt, idx) => (
                          <option key={idx} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>

                      {/* Show text input when "Others" selected */}
                      {showOthersField && (
                        <input
                          type="text"
                          placeholder="Please specify"
                          className="w-full rounded-lg border border-textYellow p-2 focus:outline-none"
                          value={value?.othersText || ""}
                          onChange={(e) =>
                            handleInputChange(currentSectionKey, q.id, {
                              option: selected,
                              othersText: e.target.value,
                            })
                          }
                        />
                      )}
                    </div>
                  );
                })()
              ) : q.showSubFieldsOnYes && q.options ? (
                // Show single-choice sub-options when "Yes" is selected
                <div className="space-y-3">
                  {/* Main Yes/No dropdown */}
                  <select
                    className="w-full rounded-lg border border-textYellow p-2 focus:outline-none"
                    value={value.option || ""}
                    onChange={(e) =>
                      handleInputChange(currentSectionKey, q.id, {
                        ...value,
                        option: e.target.value,
                        subSelection: "", // clear sub-selection when main changes
                      })
                    }
                  >
                    <option value="">Select an option</option>
                    {q.options.map((opt, i) => (
                      <option key={i} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>

                  {/* Show sub-options only when "Yes" selected */}
                  {value.option === "Yes" && q.subOptions && (
                    <div className="space-y-2 pl-3">
                      {q.subOptions.map((sub, idx) => (
                        <label key={idx} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`sub-${q.id}`}
                            checked={value.subSelection === sub}
                            onChange={() =>
                              handleInputChange(currentSectionKey, q.id, {
                                ...value,
                                subSelection: sub,
                              })
                            }
                          />
                          {sub}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ) : q.options ? (
                //  Default dropdown
                <select
                  className="w-full rounded-lg border border-textYellow bg-white p-3 focus:outline-none"
                  value={formData[currentSectionKey]?.[q.id] || ""}
                  onChange={(e) =>
                    handleInputChange(currentSectionKey, q.id, e.target.value)
                  }
                >
                  <option value="">Select an option</option>
                  {q.options.map((opt, index) => (
                    <option key={index} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                // Default textarea
                <textarea
                  className="w-full rounded-lg border border-textYellow p-3 focus:outline-none"
                  placeholder="Type your answer here..."
                  value={formData[currentSectionKey]?.[q.id] || ""}
                  onChange={(e) =>
                    handleInputChange(currentSectionKey, q.id, e.target.value)
                  }
                />
              )}
            </div>
          );
        })}

        {/* Pagination Buttons */}
        <div className="flex justify-between gap-4  pt-6 xxs:flex-row">
          <button
            onClick={() => handlePagnination("backward")}
            disabled={
              (currentSectionIndex === 0 && currentQuestionPage === 0) ||
              isButtonLoading
            }
            className={`flex w-[100px] items-center justify-center gap-2 rounded-full px-2 py-2 text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed xxs:w-[130px] xxs:px-5 ${
              currentSectionIndex === 0 && currentQuestionPage === 0
                ? "cursor-not-allowed bg-gray-200 text-gray-400"
                : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            <FaArrowLeft /> Previous
          </button>

          {/* Pagination dots */}
          <div className="flex flex-wrap items-center space-x-2">
            {[...Array(totalQuestionPages)].map((_, i) => (
              <div
                key={i}
                className={`h-3 w-3 rounded-full ${
                  i === currentQuestionPage ? "bg-textYellow" : "bg-gray-300"
                }`}
              ></div>
            ))}
          </div>

          {currentSectionIndex === sectionKeys.length - 1 &&
          currentQuestionPage === totalQuestionPages - 1 ? (
            <button
              disabled={isButtonLoading}
              className="flex w-[100px]  items-center justify-center gap-2 rounded-full bg-green-600 px-2 py-2 font-medium text-white shadow transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-400 xxs:w-[130px] xxs:px-5"
              onClick={() => setIsSubmitModalOpen(true)}
            >
              {isButtonLoading ? (
                <div className="flex flex-col items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  Submit <FaPaperPlane />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => handlePagnination("forward")}
              disabled={isButtonLoading}
              className="flex w-[130px] items-center justify-center gap-2 rounded-full bg-textYellow px-2 py-2 font-medium text-white shadow transition hover:bg-yellow-700 disabled:cursor-not-allowed disabled:bg-textYellow/50
             xxs:px-5"
            >
              {isButtonLoading ? (
                <div className="flex flex-col items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  Next <FaArrowRight />
                </>
              )}
            </button>
          )}
        </div>

        {/* Page Info */}
        <div className="flex w-full items-center justify-center gap-1 text-sm font-medium text-gray-700">
          <p>Section {currentSectionIndex + 1}</p>
          <p>/</p>
          <p>
            Page {currentQuestionPage + 1} of {totalQuestionPages}
          </p>
        </div>
        <hr className="border-2 border-textBlack" />
      </div>

      {/* Next/Previous Section Navigation */}
      <div className="mt-2 flex justify-between">
        <button
          onClick={() => handleNextSection("prev")}
          className="w-[100px] rounded bg-textYellow p-2 text-white xxs:w-[130px]"
        >
          Previous
        </button>
        <button
          onClick={() => handleNextSection("next")}
          className={`w-[100px] rounded bg-textYellow p-2 text-white xxs:w-[130px] ${
            isButtonLoading || completionPercentage < 100
              ? "cursor-not-allowed opacity-50"
              : ""
          }`}
          disabled={isButtonLoading || completionPercentage < 100}
        >
          Next
        </button>
      </div>

      {/* Guidelines Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex min-h-screen flex-col items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm overflow-y-auto">
          <div className="relative h-[95%] w-[95%] rounded-lg bg-white p-6 shadow-lg sm:h-[80%] sm:w-[80%]">
            <div className="flex h-[80%] w-full overflow-y-auto rounded-xl border-2 border-black bg-gray-50 sm:h-[90%]">
              <QuestionnaireGuidelines />
            </div>
            <div className="flex w-full flex-col items-end justify-between gap-3 p-4 sm:flex-row sm:items-center">
              <div className="flex items-center justify-center gap-3">
                <input
                  type="checkbox"
                  name="agreed"
                  id="agreed"
                  className="h-4 w-4 cursor-pointer rounded-lg"
                  onClick={() => setIsAgreed(!isAgreed)}
                />
                <label
                  htmlFor="agreed"
                  className="cursor-pointer text-sm font-medium sm:text-sm lg:text-lg"
                >
                  I confirm that I have read and understood all the
                  questionnaire guidelines.
                </label>
              </div>
              <button
                disabled={!isAgreed}
                onClick={() => setIsModalOpen(false)}
                className="w-[100px] rounded-xl bg-blue-500 p-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final Submit Confirmation Modal */}
      {isSubmitModalOpen && (
        <div className="fixed inset-0 z-50 flex min-h-screen flex-col items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm overflow-y-auto">
          <div className="relative w-[90%] rounded-lg bg-white p-6 shadow-lg sm:w-[500px]">
            <button
              onClick={() => setIsSubmitModalOpen(false)}
              className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close"
            >
              <IoClose size={20} />
            </button>
            <h2 className="mb-4 text-center text-xl font-semibold text-textGreen">
              Final Confirmation
            </h2>
            <div className="flex flex-col gap-3 text-sm text-textBlack">
              <p>
                I confirm that the information provided by me is true and
                accurate, and I agree to abide by the Terms and Conditions and
                Privacy Policy of VMB.
              </p>
              <p>
                I understand Vaishnava Marriage Bureau (VMB) acts solely as a
                facilitator for introducing prospective matches and does not
                guarantee marriage or compatibility.
              </p>
            </div>
            <div className="mt-4 flex items-start gap-3">
              <input
                type="checkbox"
                id="submitAgreed"
                className="mt-1 h-4 w-4 cursor-pointer"
                checked={isSubmitAgreed}
                onChange={() => setIsSubmitAgreed(!isSubmitAgreed)}
              />
              <label htmlFor="submitAgreed" className="cursor-pointer text-sm font-medium">
                I have read, understand, and agree to the above declarations,
                the Terms and Conditions, and the Privacy Policy of VMB.
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsSubmitModalOpen(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                disabled={!isSubmitAgreed}
                onClick={() => {
                  setIsSubmitModalOpen(false);
                  handleSubmit();
                }}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
              >
                Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
};

export default QuestionnaireForm;

